import { useState } from 'react';
import { Button, Checkbox, Tag } from '@indipia/ui-core';
import { getSiteColor } from '@indipia/shared';
import { useWorkTypeName } from '../../hooks/useWorkTypeName';
import type { BasicInfoPartner } from '../../types/worklog.types';

// ── 반장 다중선택 팝오버 (WorkOrderListPage 동명 컴포넌트와 동일 UX) ──
/** 팝오버 행에서 바로 보여주는 공종 칩 최대 개수 — 초과분은 '+N' */
const MAX_TRADE_CHIPS = 3;

export function PartnerFilterPopover({
  selected,
  partners,
  error,
  onApply,
  onClose,
}: {
  selected: BasicInfoPartner[];
  partners: BasicInfoPartner[] | null;
  error: boolean;
  onApply: (list: BasicInfoPartner[]) => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState<Set<number>>(new Set(selected.map((p) => p.partnerId)));

  const workTypeName = useWorkTypeName();

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => onApply((partners ?? []).filter((p) => checked.has(p.partnerId)));

  return (
    <>
      <div className="fixed inset-0 z-dropdown" onClick={onClose} aria-hidden />
      <div className="absolute left-0 top-full z-dropdown mt-1 w-64 rounded-lg border border-border bg-surface-raised p-3 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-body-md font-semibold text-text">작성자 필터</span>
          <Button variant="ghost" size="sm" onClick={() => setChecked(new Set())}>
            전체 해제
          </Button>
        </div>
        {/* 가로 스크롤 없음(overflow-x-hidden) — 칩이 많아도 목록 폭 안에서 줄바꿈한다 */}
        <div className="max-h-56 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {partners === null && !error && (
            <div className="py-6 text-center text-body-sm text-text-muted">불러오는 중…</div>
          )}
          {error && <div className="py-6 text-center text-body-sm text-text-muted">작성자 목록을 불러오지 못했습니다.</div>}
          {partners?.length === 0 && (
            <div className="py-6 text-center text-body-sm text-text-muted">등록된 작성자가 없습니다.</div>
          )}
          {partners?.map((p) => (
            // 행 레이아웃: [체크박스][아바타][이름 / 공종 칩(아래로 줄바꿈)] — 공종이 많은 반장도 이름이 맨 위에 보이도록
            // 위쪽 기준(items-start)으로 맞춘다(칩을 이름 옆 좁은 열에 두면 행이 길어져 세로 중앙의 이름·체크박스가 가려졌다).
            <label key={p.partnerId} className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-2 hover:bg-surface-muted">
              {/* 행 전체가 <label> 이라 bare — Checkbox 자체 label 래핑을 끄고 input만 렌더(label 중첩 방지).
                  h-7(=아바타 높이)로 감싸 첫 줄(아바타·이름) 중심에 맞춘다 */}
              <span className="flex h-7 shrink-0 items-center">
                <Checkbox bare checked={checked.has(p.partnerId)} onChange={() => toggle(p.partnerId)} />
              </span>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-body-sm font-bold text-white"
                style={{ background: getSiteColor(p.partnerId) }}
              >
                {p.partnerName.charAt(0)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex h-7 items-center gap-1.5 text-body-md font-medium text-text">
                  <span className="min-w-0 truncate">{p.partnerName}</span>
                  {/* 자재파트너 구분 — 시공 전용·사장님 항목은 표시 없음 */}
                  {(p.partnerRole === 'MATERIAL' || p.partnerRole === 'BOTH') && (
                    <Tag className="!px-1.5 !py-[1px] !text-caption">{p.partnerRole === 'BOTH' ? '시공·자재' : '자재'}</Tag>
                  )}
                </div>
                {/* 공종 칩 — 이름 아래에서 줄바꿈. 앞 MAX_TRADE_CHIPS개만 보이고 나머지는 '+N'(전체는 hover title) */}
                {p.workTypeCodes && (() => {
                  const names = p.workTypeCodes.split(',').map((code) => workTypeName.get(code) ?? code);
                  const rest = names.length - MAX_TRADE_CHIPS;
                  return (
                    <div className="flex flex-wrap gap-02 pb-01" title={names.join(', ')}>
                      {names.slice(0, MAX_TRADE_CHIPS).map((name, i) => (
                        <Tag key={`${name}-${i}`} className="!px-1.5 !py-[1px] !text-caption">
                          {name}
                        </Tag>
                      ))}
                      {rest > 0 && <Tag className="!px-1.5 !py-[1px] !text-caption">+{rest}</Tag>}
                    </div>
                  );
                })()}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>취소</Button>
          <Button size="sm" onClick={apply}>적용</Button>
        </div>
      </div>
    </>
  );
}
